/**
 * Scene-context construction module.
 * Reduces raw Three.js mesh nodes into the lightweight bounds, alias, and visibility
 * structures the viewpoint planner can search and score efficiently.
 */
import * as THREE from 'three'
import type {
  ViewpointBounds,
  ViewpointSceneContext,
  ViewpointSceneElement,
  ViewpointVector3,
} from '../generate-jig.types'
import { buildVisibilityContextForMeshNodes } from './visibility-context'

function roundValue(value: number): number {
  return parseFloat(value.toFixed(6))
}

function toVector3(value: THREE.Vector3): ViewpointVector3 {
  return {
    x: roundValue(value.x),
    y: roundValue(value.y),
    z: roundValue(value.z),
  }
}

function toBounds(box: THREE.Box3): ViewpointBounds {
  const center = new THREE.Vector3()
  const size = new THREE.Vector3()

  box.getCenter(center)
  box.getSize(size)

  return {
    center: toVector3(center),
    size: toVector3(size),
  }
}

function getUniqueAliases(aliases: string[]): string[] {
  const seen: Record<string, boolean> = {}

  return aliases.filter((alias: string) => {
    const trimmed = alias ? alias.trim() : ''
    if (!trimmed || seen[trimmed]) {
      return false
    }

    seen[trimmed] = true
    return true
  })
}

/**
 * Collapses mesh nodes into scene-wide bounds, aliasable per-element bounds, and an
 * optional mesh visibility context for precise occlusion scoring.
 */
export function buildSceneContextForMeshNodes(
  meshNodes: THREE.Mesh[],
  objectToFullName: Map<THREE.Object3D, string>
): ViewpointSceneContext | null {
  if (!meshNodes || meshNodes.length === 0) {
    return null
  }

  const elements: ViewpointSceneElement[] = []
  const elementBounds = new Map<string, ViewpointBounds>()
  const sceneBox = new THREE.Box3()
  let hasSceneBounds = false

  meshNodes.forEach((mesh: THREE.Mesh) => {
    const meshBox = new THREE.Box3().setFromObject(mesh)
    if (meshBox.isEmpty()) {
      return
    }

    const id = String(objectToFullName.get(mesh) || mesh.name || '')
    if (!id) {
      return
    }

    const aliases = getUniqueAliases([id, mesh.name || ''])
    const bounds = toBounds(meshBox)

    elements.push({
      id,
      aliases,
      bounds,
    })

    aliases.forEach((alias: string) => {
      elementBounds.set(alias, bounds)
    })

    if (!hasSceneBounds) {
      sceneBox.copy(meshBox)
      hasSceneBounds = true
      return
    }

    sceneBox.union(meshBox)
  })

  if (!hasSceneBounds || elements.length === 0) {
    return null
  }

  const visibilityContext = buildVisibilityContextForMeshNodes(meshNodes, objectToFullName)

  return {
    sceneBounds: toBounds(sceneBox),
    elements,
    elementBounds,
    visibilityContext,
  }
}
